// Proves fetes d'un esdeveniment, primer al navegador i després al servidor
// (la mateixa lògica que mobileapp/proves/provesFetesSync.js). Cada toc queda
// desat aquí al moment i es pinta sobre el que diu el servidor fins que el
// servidor l'ha desat. Els tocs s'envien d'un en un i en ordre (si una prova es
// toca dues vegades abans d'enviar-se, només s'envia l'última), de manera que
// cap resposta vella ni una càrrega que arriba tard no pot desmarcar el que
// l'usuari acaba de marcar.

export const isFetesObject = data => Boolean(data) && typeof data === 'object' && !Array.isArray(data);

const isFeta = (fetes, prova) => Boolean(prova && fetes && Object.prototype.hasOwnProperty.call(fetes, prova));

const setFeta = (fetes, prova, feta, meta = {}) => {
    const next = { ...(fetes || {}) };
    if (feta) next[prova] = { at: meta.at ?? new Date().toISOString(), by: meta.by ?? null };
    else delete next[prova];
    return next;
};

// `send(prova, { feta, by }, onSaved, onFailed)` fa la petició; `onSaved` rep { fetes, rev }
export const createProvesFetesSync = ({ send }) => {
    let server = {};
    let rev = null;
    // Snapshots que el servidor ha enviat (respostes i socket): una càrrega
    // començada abans d'algun d'aquests ja és vella
    let liveCount = 0;
    const wanted = new Map(); // prova → { feta, at, by }
    let sending = false;
    const listeners = new Set();

    const current = () => {
        let fetes = server;
        wanted.forEach((entry, prova) => {
            if (isFeta(fetes, prova) !== entry.feta) fetes = setFeta(fetes, prova, entry.feta, entry);
        });
        return fetes;
    };
    const notify = () => {
        const fetes = current();
        listeners.forEach(listener => listener(fetes));
    };

    // Snapshot amb `rev` (servidor nou): només si és més nou que l'últim
    const takeLive = (fetes, nextRev) => {
        if (!isFetesObject(fetes)) return false;
        if (Number.isFinite(nextRev)) {
            if (rev !== null && nextRev <= rev) return false;
            rev = nextRev;
        }
        liveCount += 1;
        server = fetes;
        return true;
    };

    const pump = () => {
        if (sending) return;
        const next = wanted.entries().next();
        if (next.done) return;
        const [prova, entry] = next.value;
        sending = true;
        const finish = () => {
            sending = false;
            // Si mentrestant l'han tornat a tocar, queda el toc nou per enviar
            if (wanted.get(prova) === entry) wanted.delete(prova);
            notify();
            pump();
        };
        send(prova, entry, (res = {}) => {
            takeLive(res.fetes, res.rev);
            finish();
        }, finish);
    };

    return {
        get: current,
        subscribe: listener => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        // Abans de demanar `/proves_fetes`: el que torni només val si no ha
        // arribat res més nou mentrestant
        startLoad: () => {
            const startedAt = liveCount;
            return data => {
                if (liveCount !== startedAt || !isFetesObject(data)) return;
                server = data;
                notify();
            };
        },
        receive: (fetes, nextRev) => {
            if (takeLive(fetes, nextRev)) notify();
        },
        toggle: (prova, { by = null, at = new Date().toISOString() } = {}) => {
            if (!prova) return;
            const feta = !isFeta(current(), prova);
            // Al final de la cua: s'envien en l'ordre que s'han tocat
            wanted.delete(prova);
            wanted.set(prova, { feta, at, by });
            notify();
            pump();
        },
        pending: () => wanted.size,
    };
};
